import { Dispatch, ReactElement, ReactNode, Ref, SetStateAction, forwardRef } from 'react';

import clsx from 'clsx';
import Image from 'next/image';

import Avatar, { Props as AvatarProps } from '@/common/components/Avatar/Avatar';
import { ResolutionsSrcSet } from '@/common/constants/displateRoutes';

import styles from './eventTile.module.scss';

const Badge = ({ children }: { children: ReactElement }) => (
  <div className={styles.badge} data-testid="event-tile-badge">
    {children}
  </div>
);
const BadgePlaceholder = () => (
  <div
    className={clsx(styles.badge, styles.placeholderBadge)}
    data-testid="event-tile-badge--placeholder"
  ></div>
);
const ImageBadge = ({ children }: { children: ReactElement }) => (
  <div className={styles.imageBadge} data-testid="event-tile-image-badge">
    {children}
  </div>
);

export interface EventTileProps {
  className?: string;
  title: string;
  url?: string;
  files: {
    desktopStandard: string;
    desktopRetina: string;
    mobileStandard: string;
    mobileRetina: string;
  };
  author?: {
    avatarUrl: string;
    name: string;
    nick: string;
    isLicensed: boolean;
    slug: string;
  };
  imageBadge?: ReactElement;
  badge?: ReactElement;
  vertical?: boolean;
  slotRight?: ReactNode;
  slotBottom?: ReactNode;
  setOnHold?: Dispatch<SetStateAction<boolean>>;
  onClick?: () => void;
  imageHeight?: number;
  imageWidth?: number;
  titleClassName?: string;
  avatarProps?: Partial<AvatarProps>;
  isLazyLoaded?: boolean;
  shouldShowPlaceholders?: boolean;
}

const EventTile = (
  {
    className,
    title,
    url,
    files,
    author,
    imageBadge,
    badge,
    vertical = false,
    slotRight,
    slotBottom,
    setOnHold,
    onClick,
    imageHeight,
    imageWidth,
    titleClassName,
    avatarProps,
    isLazyLoaded = true,
    shouldShowPlaceholders = true,
  }: EventTileProps,
  ref: Ref<HTMLDivElement>,
) => {
  const isInteractive = !!url;

  const handleEventLinkClick = () => {
    onClick && onClick();
  };

  return (
    <div
      className={clsx(
        styles.container,
        vertical && styles.vertical,
        isInteractive && styles.interactiveContainer,
        className,
      )}
      data-testid="event-tile"
      ref={ref}
    >
      <div className={styles.imageContainer}>
        <picture>
          <source
            //mobile
            media="(max-width: 767px)"
            srcSet={ResolutionsSrcSet({
              x1: files.mobileStandard,
              x2: files.mobileRetina,
            })}
          />
          <source
            //desktop
            media="(min-width: 768px)"
            srcSet={ResolutionsSrcSet({
              x1: files.desktopStandard,
              x2: files.desktopRetina,
            })}
          />
          {!isLazyLoaded ? (
            <Image
              alt={title}
              className={styles.image}
              data-testid="event-tile-image"
              height={imageHeight}
              onLoadingComplete={() => setOnHold && setOnHold(false)}
              priority
              src={files.mobileRetina}
              unoptimized
              width={imageWidth}
            />
          ) : (
            <img
              alt={title}
              className={styles.image}
              data-testid="event-tile-image"
              height={imageHeight}
              loading="lazy"
              onLoad={() => setOnHold && setOnHold(false)}
              src={files.mobileRetina}
              width={imageWidth}
            />
          )}
        </picture>
        {imageBadge && <ImageBadge>{imageBadge}</ImageBadge>}
      </div>
      <div className={styles.detailsContainer}>
        {badge ? <Badge>{badge}</Badge> : null}
        {!badge && shouldShowPlaceholders ? <BadgePlaceholder /> : null}
        {url ? (
          <a
            className={clsx(styles.link, styles.wholeEventClickable)}
            data-testid="event-tile-title"
            href={url}
            onClick={handleEventLinkClick}
          >
            <h3
              className={clsx(styles.title, titleClassName, {
                [styles.titleAutoHeight]: !shouldShowPlaceholders,
              })}
            >
              {title}
            </h3>
          </a>
        ) : (
          <h3
            className={clsx(styles.title, titleClassName, {
              [styles.titleAutoHeight]: !shouldShowPlaceholders,
              [styles.wholeEventClickable]: !!onClick,
            })}
            data-testid="event-tile-title"
            onClick={handleEventLinkClick}
            role="button"
          >
            {title}
          </h3>
        )}
        <div className={styles.avatarSlotContainer}>
          {author ? (
            <Avatar
              author={author}
              className={styles.authorContainer}
              imageClassName={styles.authorIcon}
              showName
              size="small"
              {...avatarProps}
            />
          ) : null}
          {!author && shouldShowPlaceholders ? <div className={styles.avatarPlaceholder} /> : null}
          {slotRight}
        </div>
        {slotBottom ? <div className={styles.slotBottom}>{slotBottom}</div> : null}
      </div>
    </div>
  );
};

export default forwardRef(EventTile);
